import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { TotpEnrollmentKey } from "../../../components/two-factor/totp-enrollment-key.js";
import { api } from "../../../lib/api.js";
import { upstreamErrorMessage } from "../../../lib/upstreamError.js";
import type { ProfileUser } from "./profileTypes.js";

/**
 * Two-factor card. When 2FA is on it shows the enabled state + a
 * password-confirmed disable; when off it offers BOTH enrolment paths (emailed
 * codes + an authenticator app). Extracted from `ProfilePage.tsx` together with
 * its three sub-flows so the whole 2FA surface lives in one file.
 */
export function TwoFactorCard({
  user,
  onChanged,
}: {
  user: ProfileUser;
  onChanged: () => void;
}): JSX.Element {
  if (user.two_factor_enabled) {
    return (
      <Card>
        <CardHeader title="Two-factor authentication" />
        <CardContent>
          <Typography sx={{ fontSize: 14, color: "success.main" }}>
            ● Enabled{user.two_factor_method ? ` (${user.two_factor_method})` : ""}
          </Typography>
          <DisableTwoFactor onChanged={onChanged} />
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader title="Two-factor authentication" />
      <CardContent sx={{ display: "grid", gap: 3 }}>
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          ○ Not enabled. Add a second factor to protect your account.
        </Typography>
        <EnableEmailTwoFactor email={user.email} onChanged={onChanged} />
        <Divider />
        <EnableTotp onChanged={onChanged} />
      </CardContent>
    </Card>
  );
}

function DisableTwoFactor({ onChanged }: { onChanged: () => void }): JSX.Element {
  const [password, setPassword] = useState("");
  const m = useMutation({
    mutationFn: async () =>
      api("/api/profile/2fa/disable", { method: "POST", body: { password } }),
    onSuccess: onChanged,
  });
  return (
    <Box sx={{ mt: 2, display: "grid", gap: 1 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        Confirm your password to disable 2FA.
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
        />
        <Button
          variant="contained"
          color="error"
          onClick={() => m.mutate()}
          disabled={!password || m.isPending}
        >
          {m.isPending ? "Disabling…" : "Disable 2FA"}
        </Button>
      </Box>
      {m.isError && (
        <Typography component="span" sx={{ fontSize: 12, color: "error.main" }}>
          {upstreamErrorMessage(m.error, "Could not disable 2FA.")}
        </Typography>
      )}
    </Box>
  );
}

function EnableEmailTwoFactor({
  email,
  onChanged,
}: {
  email: string;
  onChanged: () => void;
}): JSX.Element {
  const [sent, setSent] = useState(false);
  const [otp, setOtp] = useState("");
  const sendCode = useMutation({
    mutationFn: async () =>
      api("/api/profile/2fa/email/setup", { method: "POST", body: {} }),
    onSuccess: () => setSent(true),
  });
  const verify = useMutation({
    mutationFn: async () =>
      api("/api/profile/2fa/email/verify-setup", {
        method: "POST",
        body: { otp: otp.trim() },
      }),
    onSuccess: onChanged,
  });
  return (
    <Box>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
        Email codes
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
        Receive a one-time code at {email} on sign-in.
      </Typography>
      {!sent ? (
        <Button
          variant="contained"
          sx={{ mt: 1 }}
          onClick={() => sendCode.mutate()}
          disabled={sendCode.isPending}
        >
          {sendCode.isPending ? "Sending…" : "Send setup code"}
        </Button>
      ) : (
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="6-digit code"
            inputProps={{ inputMode: "numeric" }}
            sx={{ width: 128 }}
          />
          <Button
            variant="contained"
            onClick={() => verify.mutate()}
            disabled={!otp || verify.isPending}
          >
            {verify.isPending ? "Verifying…" : "Verify & enable"}
          </Button>
        </Box>
      )}
      {(sendCode.isError || verify.isError) && (
        <Typography component="span" sx={{ mt: 0.5, display: "block", fontSize: 12, color: "error.main" }}>
          {upstreamErrorMessage(
            sendCode.error ?? verify.error,
            "Could not set up email 2FA.",
          )}
        </Typography>
      )}
    </Box>
  );
}

function EnableTotp({ onChanged }: { onChanged: () => void }): JSX.Element {
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const begin = useMutation({
    mutationFn: async () =>
      api<{ secret: string; otpauth_url: string }>("/api/profile/2fa/enable", {
        method: "POST",
        body: {},
      }),
    onSuccess: (r) => {
      setSecret(r.secret);
      setOtpauth(r.otpauth_url);
    },
  });
  const verify = useMutation({
    mutationFn: async () =>
      api<{ backup_codes?: string[] }>("/api/profile/2fa/verify", {
        method: "POST",
        body: { otp: otp.trim() },
      }),
    onSuccess: (r) => {
      setBackupCodes(r.backup_codes ?? []);
      onChanged();
    },
  });

  return (
    <Box>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
        Authenticator app
      </Typography>
      <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
        Use an app like 1Password or Google Authenticator.
      </Typography>
      {!secret ? (
        <Button
          variant="contained"
          sx={{ mt: 1 }}
          onClick={() => begin.mutate()}
          disabled={begin.isPending}
        >
          {begin.isPending ? "Preparing…" : "Set up authenticator"}
        </Button>
      ) : backupCodes ? (
        <Box
          sx={{
            mt: 1,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "surfaceSoft",
            p: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: "text.primary" }}>
            2FA enabled. Save these backup codes:
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              whiteSpace: "pre-wrap",
              fontFamily: "ui-monospace, monospace",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {backupCodes.join("  ")}
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 1, display: "grid", gap: 1 }}>
          <TotpEnrollmentKey secret={secret} otpauthUrl={otpauth} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit code"
              inputProps={{ inputMode: "numeric" }}
              sx={{ width: 128 }}
            />
            <Button
              variant="contained"
              onClick={() => verify.mutate()}
              disabled={!otp || verify.isPending}
            >
              {verify.isPending ? "Verifying…" : "Verify & enable"}
            </Button>
          </Box>
        </Box>
      )}
      {(begin.isError || verify.isError) && (
        <Typography component="span" sx={{ mt: 0.5, display: "block", fontSize: 12, color: "error.main" }}>
          {upstreamErrorMessage(begin.error ?? verify.error, "Could not enable 2FA.")}
        </Typography>
      )}
    </Box>
  );
}
